import {
  CircularProgress,
  makeStyles,
  Paper,
  Slide,
  InputBase,
  Typography,
  Button
} from "@material-ui/core";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext
} from "react";
import SaveIcon from "@material-ui/icons/Save";
import TranslateIcon from "@material-ui/icons/Translate";
import LanguageContext from "../../contexts/LanguageContext";
import { useDefaultLanguage } from "../../hooks/useDefaultLanguage";
import { useSlideAnimation } from "../../hooks/useSlideAnimation";
import GentleGrowTransition from "../GentleGrowTransition";
import QuizEditor from "./quizEditor/QuizEditor";
import SlateEditor from "./slateEditor/SlateEditor";
import { DEFAULT_EDITOR_CONTENT } from "../../constants/defaultEditorContent";

const useStyles = makeStyles((theme) => ({
  editorContainer: {
    minHeight: 0,
    flexGrow: "1",
    position: "relative",
    "&.side-by-side": {
      margin: "1rem",
    },
    "&.quiz-mode": {
      overflowY: "auto",
    },
  },
  editorCard: {
    height: "100%",
    justifyContent: "center",
    gap: "1rem",
    display: "flex",
    "&:not(.side-by-side) $editorWithTitleWrapper": {
      width: "70%",

      [theme.breakpoints.down("md")]: {
        width: "80%",
      },
      [theme.breakpoints.down("sm")]: {
        width: "95%",
      },
    },
  },
  editorWithTitleWrapper: {
    height: "100%",
    minHeight: 0,
    width: "50%",
    display: "flex",
    flexDirection: "column",
    transition: "opacity 200ms ease",
  },
  originalContentTitle: {
    fontSize: "3.2rem",
    fontFamily: "Montserrat",
    color: "var(--garnish-dark-green)",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    padding: "1rem",
    paddingBottom: "9.7rem",
    borderBottom: `1px solid var(--dark-grey)`,
    "&.quiz-title": {
      paddingBottom: "1rem",
      marginBottom: "0",
      borderBottom: "none",
    },
  },
  originalContentLabel: {
    fontSize: "1.5rem",
    marginLeft: "auto",
    background: "var(--dark-grey)",
    padding: ".5rem 1rem",
    borderRadius: ".5rem",
    fontWeight: "normal",
    whiteSpace: "nowrap",
  },
  translationEditorWrapper: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
  },
  translatedTitleWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "1rem",
    paddingBottom: "9.7rem",
    borderBottom: `1px solid var(--dark-grey)`,
    background: "white",
    "&.quiz-title": {
      paddingBottom: "1rem",
      marginBottom: "0",
      borderBottom: "none",
    },
  },
  translatedTitleInput: {
    fontSize: "3.2rem",
    fontFamily: "Montserrat",
    color: "var(--garnish-dark-green)",
    fontWeight: "bold",
    flexGrow: 1,
    marginRight: "1rem",
    "& input::placeholder": {
      color: "#ccc",
      opacity: 1,
    },
  },
  translationContentLabel: {
    fontSize: "1.5rem",
    marginLeft: "auto",
    background: "#e8f5e9",
    color: "var(--garnish-primary-green)",
    border: "1px solid var(--garnish-primary-green)",
    padding: ".5rem 1rem",
    borderRadius: ".5rem",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  editorContentSavingLoadingSpinner: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255, 255, 255, 0.6)",
    zIndex: 200, 
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  quizHeaderContainer: {
    display: "flex",
    gap: "1rem",
    marginBottom: "1rem",
    padding: "0 1rem",
  },
  quizColumn: {
    width: "50%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    background: "#fff",
    padding: "1rem 2rem",
    marginBottom: "2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #e0e0e0",
    boxShadow: "0px 4px 6px -2px rgba(0,0,0,0.1)"
  },
  translationTitle: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    color: "var(--garnish-dark-green)",
    fontWeight: "bold",
    fontSize: "1.2rem",
    textTransform: "uppercase"
  },
  saveButton: {
    fontSize: "1.1rem",
    fontWeight: "bold",
    boxShadow: "none"
  },
  icon: {
    color: "var(--garnish-primary-green)",
    fontSize: "2rem"
  }
}));

function ContentEditor({
  saving,
  quizMode,
  editorContent,
  setEditorContent,
  languageToEditId,
  translationContent,
  setTranslatedContentForLanguageBeingEdited,
  untranslatedTitle,
  loading,
  translatedTitle,
  onTranslatedTitleChange,
  onEditQuestion,
  onAddQuestion,
  onDeleteQuestion, // FIX: Receive Prop
  onSave,
  children, 
}) {
  const classes = useStyles();
  const originalLanguageEditorRef = useRef(null);

  const getSavingLoadingSpinner = () =>
    saving ? (
      <div className={classes.editorContentSavingLoadingSpinner}>
        <CircularProgress />
      </div>
    ) : null;

  const { defaultLanguage, defaultLanguageLoaded } = useDefaultLanguage();
  const { languageInfo } = useContext(LanguageContext);
  const [originalContentScrollTop, setOriginalContentScrollTop] = useState(0);
  const [lastTranslationLanguageId, setLastTranslationLanguageId] =
    useState(null);

  const translationEditMode = useMemo(
    () => defaultLanguageLoaded && languageToEditId !== defaultLanguage.id,
    [defaultLanguageLoaded, languageToEditId, defaultLanguage]
  );

  useEffect(() => {
    if (translationContent[languageToEditId]) {
      setLastTranslationLanguageId(languageToEditId);
    }
  }, [translationContent, languageToEditId]);

  const animateOriginalLanguageEditor = useSlideAnimation(
    originalLanguageEditorRef,
    lastTranslationLanguageId,
    translationEditMode
  );

  useEffect(() => {
    if (originalLanguageEditorRef.current && !loading) {
      animateOriginalLanguageEditor();
    }
  }, [
    translationEditMode,
    lastTranslationLanguageId,
    loading,
    animateOriginalLanguageEditor,
  ]);

  const getTranslationContentForLanguageToEditId = useCallback(
    () =>
      translationContent[languageToEditId] ||
      translationContent[lastTranslationLanguageId] ||
      DEFAULT_EDITOR_CONTENT,
    [languageToEditId, translationContent, lastTranslationLanguageId]
  );

  const handleTranslationEditorScrolled = (event) => {
    setOriginalContentScrollTop(event.target.scrollTop);
  };

  const getLanguageName = () => {
    if (!languageInfo || !languageInfo.languages) return "Translation";
    const lang = languageInfo.languages.find(l => l.id === languageToEditId);
    return lang ? lang.displayName : "Translation";
  };

  const getUntranslatedTitleIfInTranslationMode = () =>
    translationEditMode ? (
      <Paper className={classes.originalContentTitle} elevation={0}>
        {untranslatedTitle}{" "}
        <span className={classes.originalContentLabel}>
          original English content
        </span>
      </Paper>
    ) : null;

  const getTranslatedTitleInput = (isQuiz = false) => (
    <Paper
      className={`${classes.translatedTitleWrapper} ${
        isQuiz ? "quiz-title" : ""
      }`}
      elevation={0}
    >
      <InputBase
        className={classes.translatedTitleInput}
        value={translatedTitle || ""}
        onChange={(e) => onTranslatedTitleChange(e.target.value)}
        placeholder="Enter translated title..."
        multiline
      />
      <span className={classes.translationContentLabel}>
        translated content
      </span>
    </Paper>
  );

  return (
    <Fragment>
      <div
        className={`${classes.editorContainer} ${
          translationEditMode ? "side-by-side" : ""
        } ${quizMode ? "quiz-mode" : ""}`}
      >
        {getSavingLoadingSpinner()}

        {quizMode && translationEditMode && (
           <Paper className={classes.stickyHeader} elevation={1}>
             <div className={classes.translationTitle}>
               <TranslateIcon className={classes.icon} />
               <span>Translating Quiz to <span style={{color:'var(--garnish-primary-green)'}}>{getLanguageName()}</span></span>
             </div>
             <Button
               variant="contained"
               color="primary"
               startIcon={<SaveIcon />}
               className={classes.saveButton}
               onClick={onSave}
               disabled={saving}
             >
               {saving ? "Saving..." : `Save ${getLanguageName()} Translations`}
             </Button>
           </Paper>
        )}

        {quizMode && translationEditMode && (
          <div className={classes.quizHeaderContainer}>
            <div className={classes.quizColumn}>
              <Paper className={`${classes.originalContentTitle} quiz-title`}>
                {untranslatedTitle}
                <span className={classes.originalContentLabel}>original</span>
              </Paper>
            </div>

            <div className={classes.quizColumn}>
              {getTranslatedTitleInput(true)}
            </div>
          </div>
        )}

        <GentleGrowTransition show={!quizMode}>
          <div
            className={`${classes.editorCard} ${
              translationEditMode ? "side-by-side" : ""
            }`}
          >
            <div
              className={classes.editorWithTitleWrapper}
              ref={originalLanguageEditorRef}
            >
              {getUntranslatedTitleIfInTranslationMode()}
              <SlateEditor
                editorContent={editorContent || DEFAULT_EDITOR_CONTENT}
                setEditorContent={setEditorContent}
                readOnly={translationEditMode}
                editorScrollTop={originalContentScrollTop}
              />
            </div>

            <Slide
              direction="up"
              in={translationEditMode}
              mountOnEnter
              unmountOnExit
            >
              <div className={classes.translationEditorWrapper}>
                {getTranslatedTitleInput(false)}

                <SlateEditor
                  editorContent={getTranslationContentForLanguageToEditId()}
                  setEditorContent={setTranslatedContentForLanguageBeingEdited}
                  onScroll={handleTranslationEditorScrolled}
                />
              </div>
            </Slide>
          </div>
        </GentleGrowTransition>

        <GentleGrowTransition show={quizMode}>
          <QuizEditor
            quizMode={quizMode}
            translationMode={translationEditMode}
            languageToEditId={languageToEditId}
            translationModeHasBeenTriggered={lastTranslationLanguageId}
            onEditQuestion={onEditQuestion}
            onAddQuestion={onAddQuestion}
            onDeleteQuestion={onDeleteQuestion} // FIX: Pass down
            onSave={onSave}
          />
        </GentleGrowTransition>
      </div>
    </Fragment>
  );
}

export default ContentEditor;